// Module-scoped token — never exposed on globalThis (XSS-reachable).
// Hydrate from sessionStorage on load so a page reload within the same
// tab restores the session without a full OIDC redirect.
let token: string | undefined = (() => {
  try {
    return sessionStorage.getItem('auth:token') || undefined;
  } catch {
    return undefined;
  }
})();

export function getToken(): string | undefined {
  if (token) return token;
  try {
    const fromSession = sessionStorage.getItem('auth:token') || undefined;
    if (fromSession) {
      token = fromSession;
      return token;
    }
  } catch {}
  return undefined;
}

// Module-scoped id_token — never on globalThis.
let idToken: string | undefined = (() => {
  try {
    return sessionStorage.getItem('auth:id_token') || undefined;
  } catch {
    return undefined;
  }
})();

export function getIdToken(): string | undefined {
  if (idToken) return idToken;
  try {
    const fromSession = sessionStorage.getItem('auth:id_token') || undefined;
    if (fromSession) {
      idToken = fromSession;
      return idToken;
    }
  } catch {}
  return undefined;
}

export function setToken(t?: string) {
  token = t;
  try {
    if (t) sessionStorage.setItem('auth:token', t);
    else sessionStorage.removeItem('auth:token');
    if (t) sessionStorage.removeItem('auth:explicit_logout');
  } catch {}
  try {
    window.dispatchEvent(new Event('ui:token-set'));
  } catch {}
}

/**
 * Check if user explicitly logged out (to prevent auto-login)
 */
export function wasExplicitLogout(): boolean {
  try {
    return sessionStorage.getItem('auth:explicit_logout') === 'true';
  } catch {
    return false;
  }
}

/**
 * Clear the explicit logout flag (e.g., when user clicks "Sign In" button)
 */
export function clearExplicitLogout(): void {
  try {
    sessionStorage.removeItem('auth:explicit_logout');
  } catch {}
}

export type OidcConfig = {
  issuer: string;
  clientId: string;
  redirectUri: string;
  scope?: string; // space-separated
  audience?: string;
  useRefreshToken?: boolean;
  clientSecret?: string; // For confidential clients
};

let oidcConfig: OidcConfig | undefined;
let refreshToken: string | undefined;
let tokenExpiresAtMs: number | undefined;
let refreshTimer: ReturnType<typeof setTimeout> | undefined;

// Race condition guards
let loginInProgress = false;
let refreshAbortController: AbortController | null = null;
let logoutInProgress = false;

// Cached OIDC discovery — avoids repeated fetches to IdP (DNS/MITM vector).
// Keyed by issuer URL. Cleared on configureOIDC (issuer change).
let wellKnownCache: Record<string, any> = {};

async function getWellKnown(issuer: string): Promise<any> {
  const key = issuer.replace(/\/$/, '');
  if (wellKnownCache[key]) return wellKnownCache[key];
  const resp = await fetch(`${key}/.well-known/openid-configuration`);
  if (!resp.ok) throw new Error(`OIDC discovery failed: ${resp.status}`);
  const data = await resp.json();
  wellKnownCache[key] = data;
  return data;
}

export function configureOIDC(config: OidcConfig) {
  // Clear cache if issuer changed (different provider)
  if (oidcConfig && oidcConfig.issuer !== config.issuer) {
    wellKnownCache = {};
  }
  oidcConfig = config;
}

function base64UrlEncode(input: ArrayBuffer): string {
  const bytes = new Uint8Array(input);
  let str = '';
  for (let i = 0; i < bytes.byteLength; i++) str += String.fromCharCode((bytes[i] ?? 0) as number);
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function sha256buffer(verifier: string): Promise<ArrayBuffer> {
  const enc = new TextEncoder();
  return crypto.subtle.digest('SHA-256', enc.encode(verifier));
}

function randomString(len = 64): string {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  const cLen = charset.length; // 66
  const maxUnbiased = 256 - (256 % cLen); // 252 — reject 252..255
  let result = '';
  while (result.length < len) {
    const randomValues = new Uint8Array(len - result.length + 16); // overshoot for rejections
    crypto.getRandomValues(randomValues);
    for (let i = 0; i < randomValues.length && result.length < len; i++) {
      if ((randomValues[i] as number) < maxUnbiased) {
        result += charset[(randomValues[i] as number) % cLen];
      }
    }
  }
  return result;
}

function ensureOidcConfiguredFromWindow(): boolean {
  try {
    // Read runtime globals if available
    const w = (globalThis as any) || {};
    const issuer = w.__OIDC_ISSUER__ as string | undefined;
    const clientId = w.__OIDC_CLIENT_ID__ as string | undefined;
    const redirectUri = w.__OIDC_REDIRECT_URI__ as string | undefined;
    const requiredRaw = w.__REQUIRED_SCOPES__ as any;
    const scope = Array.isArray(requiredRaw)
      ? requiredRaw.join(' ')
      : typeof requiredRaw === 'string'
        ? requiredRaw
        : 'openid profile email';
    if (issuer && clientId && redirectUri) {
      configureOIDC({ issuer, clientId, redirectUri, scope, useRefreshToken: true });
      return true;
    }
  } catch {}
  return false;
}

export async function login(): Promise<void> {
  console.log('[FERP-AUTH] login() called');
  // Guard against double login clicks causing state mismatch
  if (loginInProgress) {
    console.warn('[FERP-AUTH] Login already in progress, ignoring duplicate call');
    return;
  }
  loginInProgress = true;

  try {
    if (!oidcConfig) {
      const configured = ensureOidcConfiguredFromWindow();
      if (!configured) throw new Error('OIDC not configured');
    }
    const { issuer, clientId, redirectUri } = oidcConfig!;
    const scope = oidcConfig!.scope || 'openid profile email';
    // Do not auto-append offline_access; providers may reject it unless explicitly enabled

    const wellKnown = await getWellKnown(issuer);
    const authEndpoint = wellKnown.authorization_endpoint;
    const state = randomString(32);
    const nonce = randomString(32);
    const codeVerifier = randomString(64);
    const challenge = base64UrlEncode(await sha256buffer(codeVerifier));
    sessionStorage.setItem('oidc:state', state);
    sessionStorage.setItem('oidc:nonce', nonce);
    sessionStorage.setItem('oidc:code_verifier', codeVerifier);
    sessionStorage.setItem('oidc:issuer', issuer);
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: clientId,
      redirect_uri: redirectUri,
      scope,
      state,
      nonce,
      code_challenge: challenge,
      code_challenge_method: 'S256',
    });

    // NOTE: prompt=login was removed — Authentik's ferp-simple-password flow
    // returns 400 when prompt=login is sent. With an active IdP session,
    // auto-login is acceptable. Users can force re-auth via explicit logout.

    location.assign(`${authEndpoint}?${params.toString()}`);
    // Note: loginInProgress stays true since we're navigating away
  } catch (error) {
    loginInProgress = false;
    throw error;
  }
}

let callbackInProgress = false;

export async function maybeHandleRedirectCallback(): Promise<boolean> {
  console.log('[FERP-AUTH] maybeHandleRedirectCallback() called', {
    callbackInProgress,
    hasCode: new URL(location.href).searchParams.has('code'),
    hasState: new URL(location.href).searchParams.has('state'),
  });
  if (callbackInProgress) return false;
  if (!oidcConfig) {
    // Attempt to initialize from window if not yet configured
    ensureOidcConfiguredFromWindow();
  }
  if (!oidcConfig) return false;
  callbackInProgress = true;
  const url = new URL(location.href);
  const err = url.searchParams.get('error');
  const errDesc = url.searchParams.get('error_description');
  if (err) {
    try {
      sessionStorage.setItem('oidc:last_error', `${err}:${errDesc || ''}`);
    } catch {}
    // Clean URL to root to avoid re-triggering callback on refresh
    history.replaceState({}, document.title, '/');
    callbackInProgress = false;
    return false;
  }
  const code = url.searchParams.get('code');
  const state = url.searchParams.get('state');
  if (!code || !state) {
    callbackInProgress = false;
    return false;
  }
  const expected = sessionStorage.getItem('oidc:state');
  const codeVerifier = sessionStorage.getItem('oidc:code_verifier') || '';
  const storedIssuer = sessionStorage.getItem('oidc:issuer') || '';
  if (!expected || state !== expected) {
    callbackInProgress = false;
    throw new Error('OIDC state mismatch');
  }
  const { issuer, clientId, redirectUri } = oidcConfig!;
  const iss = storedIssuer || issuer;

  const wellKnown = await getWellKnown(iss);
  const tokenEndpoint: string = wellKnown.token_endpoint;
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: clientId,
    redirect_uri: redirectUri || '',
    code,
    code_verifier: codeVerifier,
  });

  // Add client secret for confidential clients
  if (oidcConfig!.clientSecret) {
    body.append('client_secret', oidcConfig!.clientSecret);
  }
  // Direct token exchange
  console.log('[FERP-AUTH] Exchanging code for tokens...');
  const resp = await fetch(tokenEndpoint, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body,
  });
  if (!resp.ok) {
    const errorText = await resp.text().catch(() => 'unknown');
    console.error('[FERP-AUTH] Token exchange failed:', resp.status, errorText);
    callbackInProgress = false;
    throw new Error(`OIDC token exchange failed: ${resp.status}`);
  }
  const json = await resp.json();
  console.log('[FERP-AUTH] Token exchange successful, got tokens');

  // Verify nonce in id_token to prevent token replay attacks.
  // The nonce was sent in the auth request and must match the id_token's nonce claim.
  const expectedNonce = sessionStorage.getItem('oidc:nonce') || '';
  if (json.id_token && expectedNonce) {
    try {
      const parts = json.id_token.split('.');
      if (parts.length === 3) {
        let seg = parts[1] as string;
        const pad = seg.length % 4;
        if (pad) seg += '='.repeat(4 - pad);
        const payload = JSON.parse(atob(seg.replace(/-/g, '+').replace(/_/g, '/')));
        if (payload.nonce !== expectedNonce) {
          callbackInProgress = false;
          throw new Error('OIDC nonce mismatch — possible token replay');
        }
      }
    } catch (e) {
      if (e instanceof Error && e.message.includes('nonce mismatch')) {
        throw e;
      }
      // If we can't parse the id_token, continue — backend validates signature
    }
  }

  token = json.access_token;
  refreshToken = json.refresh_token;
  tokenExpiresAtMs = getJwtExpiryMs(token) ?? Date.now() + 60 * 60 * 1000;
  // Reset login guard - callback completed successfully
  loginInProgress = false;
  scheduleTokenRefresh();
  // Persist tokens to sessionStorage (survives reload within tab)
  try {
    if (token) {
      sessionStorage.setItem('auth:token', token);
      // Clear explicit_logout flag - user successfully logged in
      sessionStorage.removeItem('auth:explicit_logout');
    } else {
      sessionStorage.removeItem('auth:token');
    }

    if (json.id_token) {
      idToken = json.id_token;
      sessionStorage.setItem('auth:id_token', json.id_token);
    } else {
      idToken = undefined;
      sessionStorage.removeItem('auth:id_token');
    }
  } catch {}
  // Clean URL and clear transient OIDC state BEFORE dispatching token-set event.
  // The event triggers checkSession → maybeHandleRedirectCallback re-entrantly;
  // if the code is still in the URL, the second call tries to exchange an already-consumed
  // authorization code and Authentik returns 400 invalid_grant.
  const clean = new URL(location.href);
  clean.searchParams.delete('code');
  clean.searchParams.delete('state');
  history.replaceState({}, document.title, clean.toString());
  sessionStorage.removeItem('oidc:state');
  sessionStorage.removeItem('oidc:nonce');
  sessionStorage.removeItem('oidc:code_verifier');
  sessionStorage.removeItem('oidc:issuer');
  callbackInProgress = false;
  // Notify listeners after cleanup so re-entrant calls see clean state
  window.dispatchEvent(new Event('ui:token-set'));
  return true;
}

export async function logout(): Promise<void> {
  console.log('[FERP-AUTH] logout() called', new Error().stack);
  // Prevent re-entrant logout and signal to refresh that we're logging out
  if (logoutInProgress) {
    console.warn('[FERP-AUTH] Logout already in progress');
    return;
  }
  logoutInProgress = true;

  // Cancel any in-flight token refresh FIRST to prevent race
  if (refreshAbortController) {
    refreshAbortController.abort();
    refreshAbortController = null;
  }

  // Clear refresh timer before any async operations
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = undefined;
  }

  // Get ID token before clearing (needed for OIDC end_session_endpoint)
  const idTokenHint = getIdToken();

  // Clear local state
  token = undefined;
  idToken = undefined;
  refreshToken = undefined;
  tokenExpiresAtMs = undefined;
  loginInProgress = false; // Reset login guard

  try {
    sessionStorage.removeItem('auth:token');
    sessionStorage.removeItem('auth:id_token');
    sessionStorage.setItem('auth:explicit_logout', 'true');
    window.dispatchEvent(new Event('ui:token-set'));
  } catch {}

  // For OIDC providers (Authentik, etc.), redirect to end_session_endpoint
  // to properly terminate the IdP session
  if (!oidcConfig) {
    ensureOidcConfiguredFromWindow();
  }

  if (oidcConfig) {
    try {
      const { issuer, clientId } = oidcConfig;

      // Check for Authentik provider (from runtime config)
      const authProvider = (window as any).__AUTH_PROVIDER__ as string | undefined;
      const isAuthentik = authProvider === 'authentik';

      if (isAuthentik) {
        const wellKnown = await getWellKnown(issuer);

        const endSessionEndpoint = wellKnown.end_session_endpoint;

        if (endSessionEndpoint) {
          // Use origin with trailing slash to match Authentik's strict redirect URI matching
          const postLogoutRedirectUri = window.location.origin + '/';
          const params = new URLSearchParams({
            client_id: clientId,
            post_logout_redirect_uri: postLogoutRedirectUri,
          });

          // Include id_token_hint if available (required by some providers)
          if (idTokenHint) {
            params.append('id_token_hint', idTokenHint);
          }

          // Redirect to IdP logout
          window.location.href = `${endSessionEndpoint}?${params.toString()}`;
          return;
        }
      }
    } catch (e) {
      console.warn('Failed to redirect to OIDC logout endpoint:', e);
      // Fall through to just clearing local state (already done above)
    }
  }

  // Reset logout flag for non-redirect logout (local-only logout)
  logoutInProgress = false;
}

function getJwtExpiryMs(accessToken?: string): number | undefined {
  if (!accessToken) return undefined;
  try {
    const parts = accessToken.split('.');
    if (parts.length < 2) return undefined;
    const segment: string = parts[1]!;
    const payload = JSON.parse(decodeBase64Url(segment));
    if (payload && typeof payload.exp === 'number') return payload.exp * 1000;
  } catch {}
  return undefined;
}

function decodeBase64Url(str: string | undefined): string {
  if (!str) return '';
  // padding
  const pad = str.length % 4;
  if (pad) str += '='.repeat(4 - pad);
  str = str.replace(/-/g, '+').replace(/_/g, '/');
  return atob(str);
}

async function refreshAccessToken(): Promise<void> {
  console.log('[FERP-AUTH] refreshAccessToken() called');
  if (!oidcConfig || !refreshToken) {
    console.log('[FERP-AUTH] Refresh skipped: no config or refresh token');
    return;
  }

  // Check if logout happened while we were waiting to run
  if (logoutInProgress) {
    console.log('[FERP-AUTH] Refresh skipped: logout in progress');
    return;
  }

  // Create abort controller for this refresh attempt
  refreshAbortController = new AbortController();
  const signal = refreshAbortController.signal;

  try {
    const { issuer, clientId } = oidcConfig;
    const wellKnown = await getWellKnown(issuer);
    const tokenEndpoint: string = wellKnown.token_endpoint;
    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: clientId,
      refresh_token: refreshToken || '',
    });

    // Add client secret for confidential clients
    if (oidcConfig.clientSecret) {
      body.append('client_secret', oidcConfig.clientSecret);
    }

    const resp = await fetch(tokenEndpoint, {
      method: 'POST',
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
      body,
      signal,
    });

    // Double-check logout didn't happen during fetch
    if (logoutInProgress || signal.aborted) {
      console.log('Refresh aborted: logout occurred during fetch');
      return;
    }

    if (!resp.ok) {
      // Give up and require interactive login
      console.error('[FERP-AUTH] Refresh failed with status:', resp.status, '- triggering logout');
      await logout();
      return;
    }

    const json = await resp.json();

    // Final check before applying tokens
    if (logoutInProgress || signal.aborted) {
      console.log('Refresh aborted: logout occurred before applying tokens');
      return;
    }

    token = json.access_token || token;
    if (json.refresh_token) refreshToken = json.refresh_token;
    tokenExpiresAtMs = getJwtExpiryMs(token) ?? Date.now() + 60 * 60 * 1000;
    scheduleTokenRefresh();
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      console.log('Token refresh aborted');
      return;
    }
    throw error;
  } finally {
    refreshAbortController = null;
  }
}

function scheduleTokenRefresh() {
  if (!tokenExpiresAtMs) return;
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = undefined;
  }
  const now = Date.now();
  const leadMs = 60 * 1000; // refresh 60s before expiry
  const msUntil = (tokenExpiresAtMs || now + 5 * 60 * 1000) - now - leadMs;
  const dueIn = Math.max(5_000, msUntil);
  const timer: ReturnType<typeof setTimeout> = setTimeout(() => {
    refreshAccessToken().catch(() => {});
  }, dueIn);
  refreshTimer = timer;
}

export function startAutoRefresh() {
  tokenExpiresAtMs = getJwtExpiryMs(token);
  scheduleTokenRefresh();
}

// Export AuthProvider and useAuth hook
export { default as AuthProvider, useAuth } from './AuthProvider';
export type { AuthContextValue, Permission, Role, Tenant, User } from './AuthProvider';

// Export Fetch Guard for preventing direct API calls
export {
  FetchGuardViolationError,
  clearFetchGuardViolations,
  disableFetchGuard,
  emergencyBypass,
  getFetchGuardViolations,
  initializeFetchGuard,
} from './fetch-guard';
